import { useTranslation } from 'react-i18next';
import { Check, Columns3 } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '@/lib/utils';

export type ColumnChoice = {
  key: string;
  label: string;
};

/**
 * Lets a clinician choose which optional columns a table shows.
 *
 * The patients table can carry fifteen-odd columns once a clinic declares
 * custom fields, which is more than fits a 1080p screen. Rather than pick a
 * single "right" set for everyone — clinics genuinely differ in whether they
 * scan by external ID, by report date, or by clinic — the table ships a
 * sensible default and this menu makes the rest one click away. The choice is
 * remembered per device.
 */
type Props = {
  choices: ColumnChoice[];
  isVisible: (key: string) => boolean;
  onToggle: (key: string) => void;
  onReset: () => void;
};

export const ColumnsMenu = ({ choices, isVisible, onToggle, onReset }: Props) => {
  const { t } = useTranslation();
  const hiddenCount = choices.filter((c) => !isVisible(c.key)).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" title={t('Choose columns')}>
          <Columns3 className="mr-2 h-4 w-4" />
          {t('Columns')}
          {hiddenCount > 0 && (
            <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-[0.625rem] font-medium tabular-nums text-muted-foreground">
              {choices.length - hiddenCount}/{choices.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        <div className="max-h-80 overflow-y-auto">
          {choices.map((choice) => {
            const visible = isVisible(choice.key);
            return (
              <button
                key={choice.key}
                type="button"
                role="menuitemcheckbox"
                aria-checked={visible}
                onClick={() => onToggle(choice.key)}
                className={cn(
                  'flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted',
                  visible && 'font-medium',
                )}
              >
                <span className="truncate">{choice.label}</span>
                {visible && <Check className="ml-2 h-3.5 w-3.5 shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
        <div className="border-t pt-1">
          <button
            type="button"
            onClick={onReset}
            className="w-full rounded px-2 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:bg-muted"
          >
            {t('Reset to defaults')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
